import StatCards from './ui/StatCards';
import ModalLayer from './ModalLayer';
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import './Faculties.css';

interface Faculty {
  id: number;
  code: string;
  name: string;
  description: string;
}

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  modalClassName?: string;
}

const Modal: React.FC<ModalProps> = ({ visible, onClose, children, modalClassName }) => {
  if (!visible) return null;
  return (
    <ModalLayer className="fac-modal-overlay" onClick={onClose} aria-modal="true" role="dialog">
      <div className={`fac-modal ${modalClassName || ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="fac-modal-close" onClick={onClose} aria-label="Close modal">×</button>
        {children}
      </div>
    </ModalLayer>
  );
};

const Faculties: React.FC = () => {
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Modals
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);

  // Modal messages/actions
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});

  // Selected faculty
  const [selectedFaculty, setSelectedFaculty] = useState<Faculty | null>(null);

  // Forms
  const [editForm, setEditForm] = useState<{ code: string; name: string; description: string } | null>(null);
  const initialAddForm = { code: '', name: '', description: '' };
  const [addForm, setAddForm] = useState(initialAddForm);

  // Search + pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  // Fetch
  const fetchFaculties = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch('/v1/api/systems-settings/faculties/get-all');
      const data = await response.json();
      if (Array.isArray(data)) {
        setFaculties(data);
      } else if (data?.success && Array.isArray(data.data)) {
        setFaculties(data.data);
      } else {
        setError(data?.message || 'Error fetching faculties');
      }
    } catch {
      setError('Error fetching faculties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaculties();
  }, []);

  // Helpers
  const showAlert = (msg: string) => {
    setAlertMessage(msg);
    setAlertModalVisible(true);
  };

  const handleViewFaculty = (faculty: Faculty) => {
    setSelectedFaculty(faculty);
    setViewModalVisible(true);
  };

  const handleEditFaculty = (faculty: Faculty) => {
    setSelectedFaculty(faculty);
    setEditForm({ code: faculty.code, name: faculty.name, description: faculty.description });
    setEditModalVisible(true);
  };

  const handleDeleteFaculty = (id: number) => {
    setConfirmMessage('Are you sure you want to delete this faculty?');
    setConfirmAction(() => async () => {
      try {
        const response = await fetch(`/v1/api/systems-settings/faculties/delete/${id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          showAlert('Faculty deleted successfully');
          fetchFaculties();
        } else {
          showAlert('Error deleting faculty');
        }
      } catch {
        showAlert('Error deleting faculty');
      }
      setConfirmModalVisible(false);
    });
    setConfirmModalVisible(true);
  };

  // Edit form
  const handleEditFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!editForm) return;
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedFaculty || !editForm) return;
    const payload = { code: editForm.code, name: editForm.name, description: editForm.description };
    try {
      const response = await fetch(
        `/v1/api/systems-settings/faculties/edit/${selectedFaculty.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        showAlert('Faculty updated successfully');
        setEditModalVisible(false);
        fetchFaculties();
      } else {
        showAlert('Error updating faculty');
      }
    } catch {
      showAlert('Error updating faculty');
    }
  };

  // Add form
  const handleAddFormChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/v1/api/systems-settings/faculties/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (response.ok) {
        showAlert('Faculty added successfully');
        setAddModalVisible(false);
        setAddForm(initialAddForm);
        fetchFaculties();
      } else {
        showAlert('Error adding faculty');
      }
    } catch {
      showAlert('Error adding faculty');
    }
  };

  // Search + Pagination
  const filteredFaculties = faculties.filter(f =>
    [f.name, f.code, f.description].some(v => v?.toLowerCase().includes(searchTerm.trim().toLowerCase()))
  );
  const totalPages = Math.ceil(filteredFaculties.length / perPage) || 1;
  const paginatedFaculties = filteredFaculties.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  return (
    <div className="fac-container" tabIndex={-1}>
      <header className="fac-header">
        <h2 className="fac-title">Faculties/Institutes</h2>
        <button
          className="fac-add-btn"
          onClick={() => setAddModalVisible(true)}
          aria-label="Add new faculty"
        >
          Add Faculty
        </button>
      </header>

      <StatCards items={[{label:'Faculties / institutes',value:faculties.length,total:faculties.length,meterLabel:'Accessible units'},{label:'Matching filters',value:filteredFaculties.length,total:faculties.length,meterLabel:'Current search results'}]}/>

      {/* Filter / Search / Per-page */}
      <section className="fac-filter-bar" aria-label="Filters">
        <input
          className="fac-search"
          type="search"
          placeholder="Search by name, code, or description"
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          aria-label="Search faculties"
        />
        <span className="fac-pagination-select">
          Show{' '}
          <select
            value={perPage}
            onChange={e => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Rows per page"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>{' '}
          per page
        </span>
      </section>

      {/* Table */}
      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p className="fac-error">{error}</p>
      ) : (
        <>
          <div className="fac-table-wrap" tabIndex={0}>
            <table className="fac-table" role="table">
              <thead>
                <tr>
                  <th className="col-id">ID</th>
                  <th className="col-code">Faculty Code</th>
                  <th className="col-name">Faculty Name</th>
                  <th className="col-desc">Faculty Description</th>
                  <th className="col-actions fac-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFaculties.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      <div className="fac-empty">
                        <div className="fac-empty-emoji" aria-hidden>📄</div>
                        <div>No Faculties/Institutes found</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedFaculties.map(faculty => (
                    <tr key={faculty.id}>
                      <td className="col-id">{faculty.id}</td>
                      <td className="col-code">{faculty.code}</td>
                      <td className="col-name">{faculty.name}</td>
                      <td className="col-desc">{faculty.description}</td>
                      <td className="col-actions">
                        <div className="fac-actions">
                          <button
                            onClick={() => handleViewFaculty(faculty)}
                            className="fac-action-btn"
                            aria-label={`View ${faculty.name}`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditFaculty(faculty)}
                            className="fac-action-btn"
                            aria-label={`Edit ${faculty.name}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteFaculty(faculty.id)}
                            className="fac-action-btn fac-delete-btn"
                            aria-label={`Delete ${faculty.name}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <nav className="fac-pagination-controls" aria-label="Pagination">
            <button
              className="fac-pagination-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              aria-label="Previous page"
            >
              Prev
            </button>
            <span className="pg-index">
              Page {page} / {totalPages}
            </span>
            <button
              className="fac-pagination-btn"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              Next
            </button>
            <span className="fac-results-count">
              {filteredFaculties.length} result{filteredFaculties.length === 1 ? '' : 's'}
            </span>
          </nav>
        </>
      )}

      {/* View Modal */}
      <Modal visible={viewModalVisible} onClose={() => setViewModalVisible(false)}>
        {selectedFaculty && (
          <div className="fac-modal-content">
            <h3 className="fac-modal-title">Faculty Details</h3>
            <div className="fac-kv">
              <div><strong>ID</strong><span>{selectedFaculty.id}</span></div>
              <div><strong>Code</strong><span>{selectedFaculty.code}</span></div>
              <div><strong>Name</strong><span>{selectedFaculty.name}</span></div>
              <div><strong>Description</strong><span>{selectedFaculty.description}</span></div>
            </div>
            <div className="fac-modal-actions">
              <button className="fac-secondary-btn" onClick={() => setViewModalVisible(false)}>Close</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal visible={editModalVisible} onClose={() => setEditModalVisible(false)} modalClassName="fac-large-modal">
        {editForm && (
          <form className="fac-modal-form" onSubmit={handleSaveEdit}>
            <h3 className="fac-modal-title">Edit Faculty</h3>
            <div className="fac-form-group-row">
              <div className="fac-form-group">
                <label className="modal-label">Faculty Code</label>
                <input aria-label="code"
                  type="text"
                  name="code"
                  value={editForm.code}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
            </div>
            <div className="fac-form-group-row">
              <div className="fac-form-group">
                <label className="modal-label">Faculty Name</label>
                <input aria-label="name"
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
            </div>
            <div className="fac-form-group-row">
              <div className="fac-form-group">
                <label className="modal-label">Faculty Description</label>
                <input aria-label="description"
                  type="text"
                  name="description"
                  value={editForm.description}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
            </div>
            <div className="fac-modal-actions">
              <button
                type="button"
                className="fac-secondary-btn"
                onClick={() => setEditModalVisible(false)}
              >
                Cancel
              </button>
              <button type="submit" className="fac-modal-submit">Save</button>
            </div>
          </form>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal visible={addModalVisible} onClose={() => setAddModalVisible(false)} modalClassName="fac-large-modal">
        <form className="fac-modal-form" onSubmit={handleAddSubmit}>
          <h3 className="fac-modal-title">Register New Faculty</h3>
          <hr className="fac-modal-separator" />
          <div className="fac-form-group-row">
            <div className="fac-form-group">
              <label>Faculty Code</label>
              <input aria-label="code"
                type="text"
                name="code"
                value={addForm.code}
                onChange={handleAddFormChange}
                required
              />
            </div>
            <div className="fac-form-group">
              <label>Faculty Name</label>
              <input aria-label="name"
                type="text"
                name="name"
                value={addForm.name}
                onChange={handleAddFormChange}
                required
              />
            </div>
          </div>
          <div className="fac-form-group-row">
            <div className="fac-form-group">
              <label>Faculty Description</label>
              <textarea aria-label="description"
                name="description"
                value={addForm.description}
                onChange={handleAddFormChange}
                required
                className="fac-big-textarea"
                rows={5}
              />
            </div>
          </div>
          <div className="fac-modal-actions">
            <button
              type="button"
              className="fac-secondary-btn"
              onClick={() => setAddModalVisible(false)}
            >
              Cancel
            </button>
            <button type="submit" className="fac-modal-submit">Register</button>
          </div>
        </form>
      </Modal>

      {/* Confirmation Modal */}
      <Modal visible={confirmModalVisible} onClose={() => setConfirmModalVisible(false)}>
        <div className="fac-confirm-content">
          <p>{confirmMessage}</p>
          <div className="fac-confirm-actions">
            <button className="fac-confirm-btn" onClick={() => { confirmAction(); }}>
              Yes
            </button>
            <button
              className="fac-confirm-btn fac-confirm-cancel"
              onClick={() => setConfirmModalVisible(false)}
            >
              No
            </button>
          </div>
        </div>
      </Modal>

      {/* Alert Modal */}
      <Modal visible={alertModalVisible} onClose={() => setAlertModalVisible(false)}>
        <div className="fac-alert-content">
          <p>{alertMessage}</p>
          <button className="fac-alert-btn" onClick={() => setAlertModalVisible(false)}>OK</button>
        </div>
      </Modal>
    </div>
  );
};

export default Faculties;
